import * as Sentry from '@sentry/react';
import { type Mixpanel, type Config } from 'mixpanel-browser';
import mixpanel from 'mixpanel-browser';
import { createContext, ProviderProps, useEffect, useMemo } from 'react';

type ProviderPropsWithoutValue<T> = Omit<ProviderProps<T>, 'value'>;

declare global {
  interface Window {
    mixpanel?: Mixpanel;
  }
}

const MIXPANEL_TOKEN = process.env.VITE_APP_MIXPANEL_TOKEN;

export type MixpanelContext = Mixpanel | undefined;
export const mixpanelContext = createContext<MixpanelContext>(undefined);

export interface MixpanelProviderProps extends ProviderPropsWithoutValue<MixpanelContext> {
  config?: Partial<Config>;
  name?: string;
}

export const MixpanelProvider = ({
  children,
  config: _config,
  name: _name
}: MixpanelProviderProps) => {
  const name = useMemo(() => _name ?? 'affect-frontend', [_name]);
  const config = useMemo(
    () => ({
      autocapture: false,
      // record_sessions_percent: 100,
      // record_mask_text_selector: '',
      // record_heatmap_data: true,
      ..._config
    }),
    [_config]
  );
  const value = useMemo(() => {
    const mixpanelInstance = MIXPANEL_TOKEN
      ? mixpanel.init(MIXPANEL_TOKEN, config, name)
      : undefined;
    window.mixpanel = mixpanelInstance;
    return mixpanelInstance;
  }, [config, name]);

  useEffect(() => {
    if (value && typeof value.get_session_recording_properties === 'function') {
      const properties = value.get_session_recording_properties();
      const typedProps = properties as { $replay_id?: string; $mp_replay_id?: string };
      const replayId = typedProps.$replay_id || typedProps.$mp_replay_id;

      if (replayId) {
        Sentry.setTag('mixpanelReplayId', replayId);
      }
    }
  }, [value]);

  return <mixpanelContext.Provider value={value}>{children}</mixpanelContext.Provider>;
};
